import { sortBy } from 'lodash';
import { markRaw } from 'vue';
import { DossierId } from '@/dossier/models/AbstractDossier';
import serviceEvents, { DiagnosticCreated, DiagnosticRemoved, DiagnosticUpdated } from '@/shared/services/events/serviceEvents';
import { mergeInto, removeById } from '@/shared/utils';
import { present } from '@/shared/utils/typescript';
import DiagnosticClient from '../client/DiagnosticClient';
import Job from '../models/Job';
import JobContext from '../models/JobContext';
import Diagnostic, { DiagnosticId } from '../models/diagnosis/Diagnostic';
import { DiagnosticData } from '../models/diagnosis/DiagnosticData';
import DiagnosticTemplate, { DiagnosticTemplateId } from '../models/diagnosis/DiagnosticTemplate';
import MediaFile2 from '@/general/models/MediaFile2';
import { JobId } from '@/jobs/models/AbstractJob';
import SubEntityType from '@/general/constants/SubEntityType';

class DiagnosticService {
  async loadByJob(job: Job, dossierId: DossierId): Promise<void> {
    if (job.diagnosticCount > 0) {
      const diagnostics = await DiagnosticClient.loadDiagnostics(present(job.id), dossierId);
      job.diagnostics = sortBy(diagnostics, ['createdOn', 'id']);
    }
  }

  async loadTemplates(dossierId: DossierId): Promise<Array<DiagnosticTemplate>> {
    const templates = await DiagnosticClient.loadTemplates(dossierId);
    templates.forEach(markRaw);
    return templates;
  }

  async save(data: DiagnosticData, diagnostic: Diagnostic, job: Job, context: JobContext): Promise<void> {
    const clientMethod = diagnostic.isSaved() ? DiagnosticClient.updateDiagnostic : DiagnosticClient.createDiagnostic;
    const editedDiagnostic = await clientMethod(diagnostic, data, context.dossier.id, job.id);
    mergeInto(editedDiagnostic, job.diagnostics);
    await serviceEvents.fire(diagnostic.isSaved() ? new DiagnosticUpdated(editedDiagnostic, job, context) : new DiagnosticCreated(editedDiagnostic, job, context));
  }

  async remove(diagnostic: Diagnostic, job: Job, context: JobContext): Promise<void> {
    const diagnosticId = present(diagnostic.id);
    await DiagnosticClient.remove(diagnosticId, job.id, context.dossier.id);
    removeById(job.diagnostics, diagnosticId);
    await serviceEvents.fire(new DiagnosticRemoved(diagnostic, job, context));
  }

  async addTemplate(templateId: DiagnosticTemplateId, job: Job, dossierId: DossierId): Promise<void> {
    job.diagnostics = await DiagnosticClient.addTemplate(templateId, present(job.id), dossierId);
  }

  async linkMedia(files: Array<MediaFile2>, dossierId: DossierId, jobId: JobId, diagnosticId: DiagnosticId): Promise<void> {
    await DiagnosticClient.linkMedia(files, true, dossierId, jobId, diagnosticId);
    files.forEach(f => {
      f.subEntityId = diagnosticId;
      f.subEntityType = SubEntityType.DIAGNOSTIC;
    });
  }

  async unlinkMedia(files: Array<MediaFile2>, dossierId: DossierId, jobId: JobId, diagnosticId: DiagnosticId): Promise<void> {
    await DiagnosticClient.linkMedia(files, false, dossierId, jobId, diagnosticId);
    files.forEach(f => {
      f.subEntityId = null;
      f.subEntityType = null;
    });
  }
}

export default new DiagnosticService();